<template>
    <list-container v-if="filteredAppointments.length > 0">
        <list-row v-for="appointment in availableAppointments" :key="appointment.id.toString()">
            <list-cell-task-appointment-date
                :date="appointment.startDateTime"
                :is-today="appointment.isToday"
            />

            <list-cell-text style="width: 100%">
                <template #content>
                    <flex-container
                        size="auto"
                        align-content="flex-end"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon
                            :icon="getIcon(appointment)"
                            :size="IconSize.sm"
                            :color="getIconColor(appointment)"
                        />
                        <text-decorator :size="FontSize.sm" bold>
                            {{ appointment.typeLabel ?? '-' }}
                        </text-decorator>
                        <svg-icon
                            v-if="appointment.source === 'realworks'"
                            :icon="IconRealworks"
                            :size="IconSize.sm"
                            is-custom-icon
                        />
                    </flex-container>

                    <flex-container
                        v-if="appointment.location"
                        size="auto"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon
                            :icon="LocationOnIcon"
                            :size="IconSize.xs"
                            :color="Color.gray400"
                        />
                        <text-decorator
                            :size="FontSize.xs"
                            :color="Color.gray400"
                        >
                            {{ appointment.location }}
                        </text-decorator>
                    </flex-container>

                    <flex-container
                        size="auto"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon
                            :icon="FilterIcon"
                            :size="IconSize.xs"
                            :color="Color.gray400"
                        />
                        <text-decorator
                            :size="FontSize.xs"
                            :color="Color.gray400"
                        >
                            {{ appointment.lead.pipeline.name }} &nbsp;&#x2022;
                        </text-decorator>
                        <svg-icon
                            :icon="appointment.status.equals(AppointmentStatus.CONFIRMED) ? DoneIcon : CircleIcon"
                            :size="IconSize.xs"
                            :color="Color.gray400"
                        />
                        <text-decorator
                            :size="FontSize.xs"
                            :color="Color.gray400"
                        >
                            {{ appointment.status.label }}
                        </text-decorator>
                    </flex-container>
                </template>
            </list-cell-text>

            <list-cell-text>
                <template #content>
                    <avatar-group>
                        <user-avatar
                            v-for="user in appointment.assignedUsers"
                            :key="user.id.toString()"
                            :placeholder="user.name.abbreviation"
                            :url="user.avatar ? resizedToSizeUploadUrl(user.avatar, 'lg') : ''"
                            :alt="user.name.fullName"
                        />
                    </avatar-group>
                </template>
            </list-cell-text>

            <list-cell-action-double
                :actions="[{
                    icon: IconEdit,
                    ariaLabel: 'Edit',
                    onClick: () => {
                        router.push(createRouteLocation({
                            name: 'ActionDetail',
                            params: {
                                leadId: appointment.lead.id.value,
                                actionType: 'appointment',
                                actionId: appointment.id.value,
                            },
                        }, 'lead'));
                    },
                    disabled: !appointmentIsEditable(appointment),
                }, {
                    icon: IconComment,
                    ariaLabel: 'Comment',
                    onClick: () => openCommentModal(appointment),
                    disabled: !appointment.comment,
                }]"
            />
        </list-row>
    </list-container>
    <list-message-empty v-else>
        <slot name="empty-message">
            Er zijn geen afspraken gevonden voor deze filters
        </slot>
    </list-message-empty>
    <list-footer>
        <template #left>
            <slot name="footer-left" :bindings="{ count: filteredAppointments.length }" />
        </template>
        <template #default>
            <slot name="footer-center" :bindings="{ count: filteredAppointments.length }" />
        </template>
        <template #right>
            <slot name="footer-right" :bindings="{ count: filteredAppointments.length }" />
        </template>
    </list-footer>

    <task-appointment-comment-modal
        ref="commentModal"
        :comment="commentModalAppointment?.comment ?? ''"
    >
        <template v-if="commentModalAppointment" #subheading>
            <flex-container
                align-content="flex-end"
                align-items="center"
                :gap="Size.xs"
            >
                <svg-icon
                    :icon="getIcon(commentModalAppointment)"
                    :size="IconSize.sm"
                    :color="getIconColor(commentModalAppointment)"
                />
                <text-decorator :size="FontSize.sm">
                    {{ commentModalAppointment.typeLabel ?? '-' }}
                </text-decorator>
                <svg-icon
                    v-if="commentModalAppointment.source === 'realworks'"
                    :icon="IconRealworks"
                    :size="IconSize.sm"
                    is-custom-icon
                />
            </flex-container>
        </template>
    </task-appointment-comment-modal>
</template>

<script setup lang="ts">
    import {
        Color,
        type ExposedOverlay,
        FontSize,
        ListCellActionDouble,
        ListCellText,
        ListContainer,
        ListRow,
        ListMessageEmpty,
        TextDecorator,
        UserAvatar,
        AvatarGroup,
        Size,
        IconSize,
        SvgIcon,
        FlexContainer,
    } from '@keyvue/one-ui';

    import DoneIcon from '@keyvue/one-material-icons/icons/done.svg?component';
    import CircleIcon from '@keyvue/one-material-icons/icons/circle.svg?component';
    import LocationOnIcon from '@keyvue/one-material-icons/icons/location_on.svg?component';
    import FilterIcon from '@keyvue/one-material-icons/icons/filter_alt.svg?component';
    import ApartmentIcon from '@keyvue/one-material-icons/icons/apartment.svg?component';
    import PhoneIcon from '@keyvue/one-material-icons/icons/call.svg?component';
    import VideoCamIcon from '@keyvue/one-material-icons/icons/videocam.svg?component';
    import HandshakeIcon from '@keyvue/one-material-icons/icons/handshake.svg?component';

    import IconRealworks from '@/assets/icons/Realworks.svg?component';
    import IconEdit from '@keyvue/one-material-icons/icons/edit.svg?component';
    import IconComment from '@keyvue/one-material-icons/icons/sms.svg?component';

    import type { Appointment } from '@keyvue/one-api-client';
    import { resizedToSizeUploadUrl } from '@/utils/resize';
    import { computed, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import useParentRoute from '@composables/useParentRoute';
    import ListCellTaskAppointmentDate from '@components/List/Cells/Actions/ListCellTaskAppointmentDate.vue';
    import TaskAppointmentCommentModal from '@components/Modals/TaskAppointmentCommentModal.vue';
    import { AppointmentStatus, AppointmentType } from '@keyvue/one-api-client';
    import useClient from '@composables/useClient';

    const props = defineProps<{
        appointments: Appointment[],
        showAll?: boolean,
        responsibleFilter: 'user' | 'team',
    }>();

    const { currentCompany, currentUser } = useClient();

    const router = useRouter();
    const createRouteLocation = useParentRoute();

    const availableAppointments = computed(() => (
        props.showAll ? filteredAppointments.value : [...filteredAppointments.value].splice(0, 4)
    ));

    const filteredAppointments = computed(() => {
        if (props.responsibleFilter === 'user') {
            return [...sortedAppointments.value].filter(
                (filteredAppointment) => [...filteredAppointment.assignedUsers].map(
                    (filterAppointmentUsers) => filterAppointmentUsers.id.toString(),
                ).includes(currentUser.value?.id.toString() ?? ''),
            );
        }

        return [...sortedAppointments.value].filter(
            (filteredAppointment) => ![...filteredAppointment.assignedUsers].map(
                (filterAppointmentUsers) => filterAppointmentUsers.id.toString(),
            ).includes(currentUser.value?.id.toString() ?? ''),
        );
    });

    const sortedAppointments = computed(() => (
        [...props.appointments].sort((a, b) => ((new Date(b.startDateTime)).valueOf() - (new Date(a.startDateTime)).valueOf()))
    ));

    const appointmentIsEditable = (appointment: Appointment) => {
        // Check if appointment is editable at all
        if (appointment.source) {
            return false;
        }

        // Check if appointment is assigned to a user of the current company
        if (appointment.assignedUsers.length > 0) {
            return appointment.assignedUsers.some((assignedUser) => {
                if (!assignedUser.company) {
                    return false;
                }

                return assignedUser.company.equals(currentCompany.value);
            });
        }

        // Check if lead is assigned to the current company
        return appointment.lead.assignee.company.equals(currentCompany.value);
    };

    const commentModal = ref<ExposedOverlay>();
    const commentModalAppointment = ref<Appointment | null>();

    const openCommentModal = (appointment: Appointment) => {
        commentModalAppointment.value = appointment;
        commentModal.value?.openOverlay();
    };

    /** niet boos worden, eslint wilt het zo */
    const getIcon = (appointment: Appointment) => {
        switch (appointment.type?.value) {
        case AppointmentType.PHONE.value:
            return PhoneIcon;
        case AppointmentType.VIDEO.value:
            return VideoCamIcon;
        case AppointmentType.OFFICE.value:
            return ApartmentIcon;
        case AppointmentType.CONTACT.value:
            return HandshakeIcon;
        default:
            return '';
        }
    };

    /** niet boos worden, eslint wilt het zo */
    const getIconColor: (appointment: Appointment) => Color = (appointment: Appointment) => {
        switch (appointment.type?.value) {
        case AppointmentType.PHONE.value:
            return Color.brandPurple;
        case AppointmentType.VIDEO.value:
            return Color.brandPink;
        case AppointmentType.OFFICE.value:
            return Color.yellow500;
        case AppointmentType.CONTACT.value:
            return Color.blue500;
        default:
            return Color.gray400;
        }
    };
</script>
